import styled from 'styled-components';
import { Icon } from '@workramp/ui';
import { media, theme as legacyTheme } from '../../styles';
import ColorUtils from '../../common/color-utils';

/** @deprecated prefer `import { Button } from '@workramp/ui';` */
const WRButton = (props) => {
  // We use #, button to tell the browser to include this in the tab order (for accessibility)
  let {
    href,
    role,
    newTab,
    leftIcon,
    rightIcon,
    leftIconName,
    rightIconName,
    allowDefaultOnClick,
    useModernButton,
    tabIndex = 0,
    onClick = () => {},
  } = props;

  const preventDefault =
    !allowDefaultOnClick && (!href || href === '#' || props.disabled)
      ? (e) => {
          e.preventDefault();
        }
      : () => {};

  if (!href) {
    href = '#';
  }
  if (href === '#' && !role) {
    role = 'button';
  }
  if (props.disabled) {
    href = null;
    onClick = () => {};
  }
  if (props.submit) {
    return (
      <WRButtonButton
        {...props}
        href={href}
        onClick={(e) => {
          preventDefault(e);
          onClick(e);
        }}
        role={role}
      />
    );
  }
  if (useModernButton) {
    // @cmorford, this should just be its own component, leveraging wrbutton for a deadline

    return (
      <WRButtonModern
        href={href}
        onClick={(e) => {
          preventDefault(e);
          onClick(e);
        }}
        role={role}
        target={newTab && '_blank'}
      >
        {leftIconName && (
          <ModernLeftIcon>
            <Icon name={leftIconName} />
          </ModernLeftIcon>
        )}
        {props.children}
        {rightIconName && (
          <ModernRightIcon>
            <Icon name={rightIconName} />
          </ModernRightIcon>
        )}
      </WRButtonModern>
    );
  }
  return (
    <WRButtonA
      {...props}
      href={href}
      onClick={(e) => {
        preventDefault(e);
        onClick(e);
      }}
      role={role}
      target={newTab && '_blank'}
      tabIndex={tabIndex}
      onKeyDown={(e) => {
        const { key } = e;
        if (key === 'Enter' || key === ' ') {
          preventDefault(e);
          onClick(e);
        }
      }}
    >
      {leftIcon && <StyledIcon image={leftIcon} position="left" large={props.large} />}
      {props.children}
      {rightIcon && <StyledIcon image={rightIcon} position="right" large={props.large} />}
    </WRButtonA>
  );
};

const colors = (background, hover, text, border) => `
    color: ${text};
    &:link, &:visited, &:active {
      color: ${text};
    }

    background-color: ${background};
    &:hover, &:active {
      background-color: ${hover};
    }

    ${border ? `box-shadow: inset 0px 0px 0px 1px ${border}` : null}
  `;

const getStylesForColor = (props) => {
  if (props.disabled) {
    return colors('#B5B5B5', '#B5B5B5', legacyTheme.color.textPrimary);
  }
  if (props.light) {
    return colors(legacyTheme.color.lightBackground, '#EFEFEF', legacyTheme.color.textPrimary);
  }
  if (props.white) {
    return colors('white', '#EFEFEF', legacyTheme.color.textPrimary, '#d7d7d7');
  }
  if (props.bgColor) {
    const hoverColor = props.hoverColor ? props.hoverColor : ColorUtils.lightenDarkenColor(props.bgColor, 15);
    const textColor = ColorUtils.getTextColorForHex(props.bgColor);
    return colors(props.bgColor, hoverColor, textColor);
  }
  return colors(legacyTheme.color.darkBlue, '#205bb6', 'white');
};

const getStylesForShape = (props) => {
  if (props.rounded) {
    if (props.large) {
      return `
        border-radius: 50px;
        padding: 2px 28px 0;
        line-height: 2.6rem;
        ${media.mobile} {
          line-height: 2rem;
          padding-left: 18px;
          padding-right: 18px;
        }
      `;
    }
    return `
        border-radius: 15px;
      `;
  }
  return `
      border-radius: 3px;
    `;
};

const styles = (props) => {
  const paddingTop = 0;
  const paddingBottom = 0;
  let paddingLeft = 0;
  let paddingRight = 0;

  if (props.large) {
    paddingRight = 40;
    paddingLeft = 40;
    if (props.leftIcon) paddingLeft += 16;
    if (props.rightIcon) paddingRight += 16;
  } else {
    paddingRight = 20;
    paddingLeft = 20;
    if (props.leftIcon) paddingLeft += 16;
    if (props.rightIcon) paddingRight += 16;
  }

  return `
    position:relative;
    display: inline-block;
    letter-spacing: 0.5px;
    border: 0;
    outline: none;
    text-decoration: none !important;
    white-space: nowrap;
    cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
    ${legacyTheme.font.regular}
    line-height: ${props.large ? '2.5rem' : '1.9rem'};
    font-size: ${props.large ? '1rem' : '0.9rem'};
    padding: ${`${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`};

    &:focus-visible {
        outline: -webkit-focus-ring-color auto 1px;
    }

    ${getStylesForShape(props)}
    ${getStylesForColor(props)}
  `;
};

const StyledIcon = styled.div`
  background-color: white;
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  top: 3px;
  ${(props) => props.position == 'left' && 'left:  4px;'}
  ${(props) => props.position == 'right' && 'right: 4px;'}
  background-image: url(${(props) => props.image});
  background-size: 50% 50%;
  background-repeat: no-repeat;
  background-position: center center;
`;

const WRButtonA = styled.a`
  ${(props) => styles(props)}
`;

const WRButtonModern = styled.button`
  // @cmorford, i'm so sorry, tight deadline
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.colors.surface[0]};
  border: 1px solid ${({ theme }) => theme.colors.on[100]};
  box-sizing: border-box;
  box-shadow: 0px 1px 2px rgba(19, 22, 25, 0.05);
  border-radius: 8px;
  min-width: 250px;
  padding: 11px 12px;
`;

const ModernLeftIcon = styled.span`
  margin-right: 8px;
`;

const ModernRightIcon = styled.span`
  position: absolute;
  right: 12px;
`;

const WRButtonButton = styled.button`
  ${(props) => styles(props)}
`;

export default WRButton;
